<template lang="pug">
.datepicker-wrapper.d-flex.flex-column.flex-auto
  a.datepicker.custom-date-picker(
    ref="picker"
    href="#"
    @click.prevent=""
  )
    input.form-control(
      :class="{ 'all-dates-default': showPlaceholderDefault }"
      data-auto-update-input="false"
      :data-date="datepickerFormattedStartDate"
      :data-start-week-on="startWeekOn"
      :value="dateRange"
      readonly=""
      data-testid="date-range-picker-input"
      :disabled="disabled"
    )
  a.clear-range(
    v-show="hasClear && hasDates"
    href="#"
    @click.prevent="$emit('clear')"
  )
    i.hi.hi-close.hi-12
</template>

<script>
import { formatDate, formatFor } from '~/utils/time_utils';

const ObjectOrNullValidator = (p) => typeof p === 'object' || p === null || p === undefined;

export default {
  name: 'AppDateRangePicker',
  props: {
    startDate: {
      required: true,
      validator: ObjectOrNullValidator,
    },
    endDate: {
      required: true,
      validator: ObjectOrNullValidator,
    },
    startWeekOn: {
      type: Number,
      default: 0,
    },
    minDate: {
      type: String,
      default: '',
    },
    maxDate: {
      type: String,
      default: '',
    },
    hasClear: {
      type: Boolean,
      default: false,
    },
    opens: {
      type: String,
      default: 'left',
    },
    position: {
      type: String,
      default: 'relative',
    },
    dateLimit: {
      type: Number,
      default: 0,
    },
    dateFormat: {
      type: String,
      default: null,
    },
    emptyRangeText: {
      type: String,
      default: null,
    },
    computeMonthlyExclusion: {
      type: Boolean,
      default: false,
    },
    computeBiWeeklyExclusion: {
      type: Boolean,
      default: false,
    },
    excludeMonthlyRanges: {
      type: Boolean,
      default: false,
    },
    async: {
      type: Boolean,
      default: true,
    },
    showPlaceholderDefault: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    customRanges: {
      type: Array, // Sample: [{ label: 'Some label', from: Date('2023-01-01'), to: Date('2023-01-15') }]
      default() {
        return [];
      },
    },
    currentZone: {
      type: Object,
      required: true,
      default() {
        return { name: moment.tz.guess() };
      },
    },
  },
  emits: ['clear', 'change'],
  data: () => ({
    dateRangePicker: null,
  }),
  computed: {
    excludeBiweeklyRange() {
      return this.computeBiWeeklyExclusion ? !!(this.dateLimit && this.dateLimit < 14) : false;
    },
    shouldExcludeMonthlyRanges() {
      return (
        this.excludeMonthlyRanges || (this.computeMonthlyExclusion ? !!(this.dateLimit && this.dateLimit < 28) : false)
      );
    },
    hasDates() {
      return this.startDate && this.endDate;
    },
    datepickerFormattedStartDate() {
      return this.startDate ? this.startDate._i : null;
    },
    dateRange() {
      if (!(this.startDate && this.endDate) || this.showPlaceholderDefault) {
        return this.emptyRangeText ? this.emptyRangeText : 'No range selected';
      }

      const startDate = this.dateFormat ? this.startDate.format(this.dateFormat) : formatDate(this.startDate);
      const endDate = this.dateFormat ? this.endDate.format(this.dateFormat) : formatDate(this.endDate);

      return `${startDate} - ${endDate}`;
    },
    timeZone() {
      return this.currentZone.name || this.currentZone.for;
    },
  },
  watch: {
    startDate: {
      immediate: true,
      handler(newStartDate) {
        if (this.dateRangePicker) {
          this.dateRangePicker.setStartDate(moment.tz(newStartDate, this.timeZone));
        }
      },
    },
    endDate: {
      immediate: true,
      handler(newStopDate) {
        if (this.dateRangePicker) {
          this.dateRangePicker.setEndDate(moment.tz(newStopDate, this.timeZone));
        }
      },
    },
    maxDate: {
      immediate: true,
      handler(newDate) {
        if (this.dateRangePicker) {
          this.dateRangePicker.maxDate = moment.tz(newDate, this.timeZone);
        }
      },
      startWeekOn: {
        immediate: true,
        handler() {
          if (this.dateRangePicker) {
            this.refreshDatePickerRange();
          }
        },
      },
    },
    currentZone: {
      handler() {
        this.mount();
      },
    },
  },
  mounted() {
    this.mount();
  },
  beforeUnmount() {
    if (this.position === 'absolute' && $.fn.daterangepicker) {
      $(this.$el).daterangepicker('remove');
    }
  },
  methods: {
    async mount() {
      if (this.async) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      this.$nextTick(() => {
        const $rangePicker = $(this.$refs.picker);

        $rangePicker.daterangepicker(this.pickerOptions());

        this.dateRangePicker = $rangePicker.data('daterangepicker');
        $rangePicker.on('show.daterangepicker', (ev, picker) => {
          this.analyticsTrack('DatePicker opened');

          if (!$rangePicker.find('input').val() || $rangePicker.find('input').val() === 'All time') {
            picker.container.find('.ranges li').removeClass('active');
          }
        });

        $rangePicker.on('apply.daterangepicker', (ev, picker) => {
          this.analyticsTrack('Date apply', picker);
          this.gotoDateRange(picker.startDate, picker.endDate);
        });
      });
    },
    gotoDateRange(start, end) {
      if (start === undefined || end === undefined) {
        return false;
      }

      this.$emit('change', { start, end });
    },
    // Refresh values that depend on organization startWeekOn
    refreshDatePickerRange() {
      const startWeek = toMomentIsoWeekday(this.startWeekOn);

      this.dateRangePicker.ranges['Last week'] = [
        this.now().subtract(1, 'weeks').startOfIsoWeek(startWeek),
        this.now().subtract(1, 'weeks').endOfIsoWeek(startWeek),
      ];

      if (!this.excludeBiweeklyRange) {
        this.dateRangePicker.ranges['Last 2 weeks'] = [
          this.now().subtract(2, 'weeks').startOfIsoWeek(startWeek),
          this.now().subtract(1, 'weeks').endOfIsoWeek(startWeek),
        ];
      }
    },
    pickerOptions() {
      const options = {
        alwaysShowCalendars: true,
        opens: this.opens,
        locale: {
          format: formatFor('date'),
          firstDay: toMomentWeekday(this.startWeekOn),
        },
        linkedCalendars: false,
        template:
          '<div class="daterangepicker dropdown-menu">' +
          '<div class="calendar left">' +
          '<div class="daterangepicker_input">' +
          '<div class="calendar-time">' +
          '<div></div>' +
          '<i class="fa fa-clock-o glyphicon glyphicon-time"></i>' +
          '</div>' +
          '</div>' +
          '<div class="calendar-table"></div>' +
          '<input class="input-mini" disabled="disabled" type="text" name="daterangepicker_start" value="" />' +
          '</div>' +
          '<div class="calendar right">' +
          '<div class="daterangepicker_input">' +
          '<div class="calendar-time">' +
          '<div></div>' +
          '<i class="fa fa-clock-o glyphicon glyphicon-time"/>' +
          '</div>' +
          '</div>' +
          '<div class="calendar-table"></div>' +
          '<input class="input-mini" disabled="disabled" type="text" name="daterangepicker_end" value="" />' +
          '</div>' +
          '<div class="ranges d-flex flex-column justify-space-between">' +
          '<div class="range_inputs">' +
          '<button class="applyBtn" disabled="disabled" type="button"></button> ' +
          '<button class="cancelBtn" type="button"></button>' +
          '</div>' +
          '</div>' +
          '</div>',
      };

      if (this.position !== 'absolute') {options.parentEl = this.$el;}

      options.startDate = this.toMoment(this.startDate);
      options.endDate = this.toMoment(this.endDate).endOf('day');

      if (this.minDate) {
        options.minDate = this.toMoment(this.minDate);
      }

      if (this.maxDate) {
        options.maxDate = this.toMoment(this.maxDate);
      }
      // For proper calculations inside of the datepicker.js
      // the dates should start at 00:00:00 and end at 23:59:59
      let ranges = {
        Today: [this.now().startOf('day'), this.now().endOf('day')],
        Yesterday: [this.now().subtract(1, 'days').startOf('day'), this.now().subtract(1, 'days').endOf('day')],
      };

      this.customRanges.forEach((item) => {
        // TODO: Pay period is initialized in the browser timezone,
        //  it should be initialized in company timezone and then converted to the current timezone
        // moment.tz(this.minDate, this.companyZone.name).tz(this.timeZone)
        const from = item.from.isBefore(this.minDate) ? this.minDate : item.from;
        const to = this.maxDate && item.to.isAfter(this.maxDate) ? this.maxDate : item.to;

        ranges[item.label] = [this.toMoment(from), this.toMoment(to)];
      });

      ranges['Last 7 days'] = [this.now().subtract(6, 'days').startOf('day'), this.now().endOf('day')];

      const startWeek = toMomentIsoWeekday(this.startWeekOn);

      ranges['Last week'] = [
        this.now().subtract(1, 'weeks').startOfIsoWeek(startWeek),
        this.now().subtract(1, 'weeks').endOfIsoWeek(startWeek).endOf('day'),
      ];

      if (!this.excludeBiweeklyRange) {
        ranges['Last 2 weeks'] = [
          this.now().subtract(2, 'weeks').startOfIsoWeek(startWeek),
          this.now().subtract(1, 'weeks').endOfIsoWeek(startWeek).endOf('day'),
        ];
      }

      ranges['This month'] = [
        this.now().startOf('month'),
        this.maxDate ? options.maxDate.endOf('day') : this.now().endOf('month').endOf('day'),
      ];
      ranges['Last month'] = [
        this.now().subtract(1, 'month').startOf('month'),
        this.now().subtract(1, 'month').endOf('month'),
      ];

      // Remove monthly ranges if excludeMonthlyRanges is true
      if (this.excludeMonthlyRanges) {
        const { 'This month': removed1, 'Last month': removed2, ...filteredRanges } = ranges;

        ranges = filteredRanges;
      }

      options.ranges = ranges;

      if (this.dateLimit) {
        // "dateLimit - 1" as the daterangepicker does not take into account the start date itself
        options.dateLimit = { days: this.dateLimit - 1 };
      }

      options.zone = this.timeZone;

      return options;
    },
    analyticsTrack(eventName, datepicker) {
      const props = {};

      if (datepicker) {
        props.start_day_from_today = this.now().diff(datepicker.startDate, 'days');
        props.end_day_from_today = this.now().diff(datepicker.endDate, 'days');
        props.days_in_range = this.toMoment(datepicker.endDate).diff(datepicker.startDate, 'days') + 1;
        props.preset_used = !!datepicker.chosenLabel;
        datepicker.chosenLabel && (props.preset_value = datepicker.chosenLabel);
      }

      window.analytics?.track(eventName, { type: 'range', ...props });
    },
    now() {
      return this.toMoment();
    },
    toMoment(date) {
      return moment(date).tz(this.timeZone, true);
    },
  },
};
</script>
